import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

const BillingContext = createContext();

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within BillingProvider');
  }
  return context;
};

export const BillingProvider = ({ children }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gstSettings, setGstSettings] = useState({
    gstNumber: '',
    gstEnabled: false,
    cgst: 2.5,
    sgst: 2.5,
    igst: 5,
    companyName: 'Odisha Zaika',
    address: '',
    panNumber: '',
    sacCode: '996331' // Restaurant service SAC code
  });

  // Load GST settings
  useEffect(() => {
    const loadGSTSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'gst'));
        if (settingsDoc.exists()) {
          setGstSettings(prev => ({ ...prev, ...settingsDoc.data() }));
        }
      } catch (error) {
        console.error('Error loading GST settings:', error);
      }
    };
    loadGSTSettings();
  }, []);

  // Load invoices
  useEffect(() => {
    const q = query(
      collection(db, 'invoices'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const invoiceData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInvoices(invoiceData);
      setLoading(false);
    }, (error) => {
      console.error('Error loading invoices:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Generate invoice number
  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `INV-${year}${month}-${random}`;
  };

  // Calculate GST
  const calculateGST = (amount, isInterState = false) => {
    if (!gstSettings.gstEnabled) {
      return {
        subtotal: amount,
        cgst: 0,
        sgst: 0,
        igst: 0,
        total: amount,
        taxAmount: 0
      };
    }

    const subtotal = amount;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;

    if (isInterState) {
      igst = (subtotal * gstSettings.igst) / 100;
    } else {
      cgst = (subtotal * gstSettings.cgst) / 100;
      sgst = (subtotal * gstSettings.sgst) / 100;
    }

    const taxAmount = cgst + sgst + igst;
    const total = subtotal + taxAmount;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      cgst: parseFloat(cgst.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      igst: parseFloat(igst.toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    };
  };

  // Create invoice
  const createInvoice = async (invoiceData) => {
    try {
      const invoiceNumber = generateInvoiceNumber();

      // Calculate GST
      const gstCalculation = calculateGST(
        invoiceData.subtotal,
        invoiceData.isInterState || false
      );

      const newInvoice = {
        ...invoiceData,
        invoiceNumber,
        ...gstCalculation,
        status: 'unpaid',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        gstEnabled: gstSettings.gstEnabled,
        gstNumber: gstSettings.gstNumber
      };

      const docRef = await addDoc(collection(db, 'invoices'), newInvoice);
      console.log('✅ Invoice created:', docRef.id);
      return { success: true, id: docRef.id, invoiceNumber };
    } catch (error) {
      console.error('❌ Error creating invoice:', error);
      return { success: false, error: error.message };
    }
  };

  // Update invoice
  const updateInvoice = async (invoiceId, updates) => {
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);

      // Recalculate GST if amount changed
      if (updates.subtotal) {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        const gstCalculation = calculateGST(
          updates.subtotal,
          updates.isInterState || invoice.isInterState || false
        );
        updates = { ...updates, ...gstCalculation };
      }

      await updateDoc(invoiceRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      console.log('✅ Invoice updated:', invoiceId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating invoice:', error);
      return { success: false, error: error.message };
    }
  };

  // Delete invoice
  const deleteInvoice = async (invoiceId) => {
    try {
      await deleteDoc(doc(db, 'invoices', invoiceId));
      console.log('✅ Invoice deleted:', invoiceId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting invoice:', error);
      return { success: false, error: error.message };
    }
  };

  // Update GST settings
  const updateGSTSettings = async (settings) => {
    try {
      const settingsRef = doc(db, 'settings', 'gst');
      await updateDoc(settingsRef, {
        ...settings,
        updatedAt: serverTimestamp()
      });
      setGstSettings(prev => ({ ...prev, ...settings }));
      console.log('✅ GST settings updated');
      return { success: true };
    } catch (error) {
      // If document doesn't exist, create it
      try {
        await addDoc(collection(db, 'settings'), {
          ...settings,
          id: 'gst',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setGstSettings(prev => ({ ...prev, ...settings }));
        return { success: true };
      } catch (createError) {
        console.error('❌ Error updating GST settings:', createError);
        return { success: false, error: createError.message };
      }
    }
  };

  // Get invoice statistics
  const getInvoiceStats = () => {
    const totalInvoices = invoices.length;
    const paidInvoices = invoices.filter(inv => inv.status === 'paid').length;
    const unpaidInvoices = invoices.filter(inv => inv.status === 'unpaid').length;
    const overdueInvoices = invoices.filter(inv => inv.status === 'overdue').length;

    const totalAmount = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const paidAmount = invoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + (inv.total || 0), 0);
    const unpaidAmount = invoices
      .filter(inv => inv.status === 'unpaid' || inv.status === 'overdue')
      .reduce((sum, inv) => sum + (inv.total || 0), 0);

    return {
      totalInvoices,
      paidInvoices,
      unpaidInvoices,
      overdueInvoices,
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      paidAmount: parseFloat(paidAmount.toFixed(2)),
      unpaidAmount: parseFloat(unpaidAmount.toFixed(2))
    };
  };

  // Mark invoice as paid
  const markAsPaid = async (invoiceId, paymentDetails = {}) => {
    return await updateInvoice(invoiceId, {
      status: 'paid',
      paidAt: serverTimestamp(),
      paymentMethod: paymentDetails.method || 'cash',
      paymentReference: paymentDetails.reference || '',
      ...paymentDetails
    });
  };

  // Generate payment link (placeholder for Razorpay integration)
  const generatePaymentLink = async (invoiceId) => {
    try {
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        throw new Error('Invoice not found');
      }

      // In production, integrate with Razorpay Payment Links API
      const paymentLink = `https://pay.odishazaika.com/invoice/${invoice.invoiceNumber}`;

      await updateInvoice(invoiceId, {
        paymentLink,
        paymentLinkGenerated: true
      });

      return { success: true, paymentLink };
    } catch (error) {
      console.error('❌ Error generating payment link:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    invoices,
    loading,
    gstSettings,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    updateGSTSettings,
    getInvoiceStats,
    calculateGST,
    markAsPaid,
    generatePaymentLink,
    generateInvoiceNumber
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};
